"use client";

import { useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Phone } from "lucide-react";
import { useChatStore } from "@/lib/chat-store";
import { cn } from "@/lib/utils";
import { ChatBubble } from "./ChatBubble";
import { ChatInput } from "./ChatInput";
import { TypingIndicator } from "./TypingIndicator";

interface ChatViewProps {
  onBack: () => void;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatDateSeparator(ts: number): string {
  const now = new Date();
  const date = new Date(ts);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() === today.getTime()) return "Hari Ini";
  if (msgDate.getTime() === yesterday.getTime()) return "Kemarin";

  const months = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

export function ChatView({ onBack }: ChatViewProps) {
  const { conversations, activeConversationId, isAdmin, toggleAI } = useChatStore();
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const conversation = useMemo(
    () => conversations.find((c) => c.id === activeConversationId),
    [conversations, activeConversationId],
  );

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation?.messages.length, conversation?.isTyping]);

  // Scroll to bottom on mount
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "instant" });
  }, [activeConversationId]);

  // Group messages by date
  const dateGroups = useMemo(() => {
    if (!conversation) return [];

    const groups: { label: string; messages: typeof conversation.messages }[] =
      [];
    let currentLabel = "";

    for (const msg of conversation.messages) {
      const label = formatDateSeparator(msg.timestamp);
      if (label !== currentLabel) {
        currentLabel = label;
        groups.push({ label, messages: [] });
      }
      groups[groups.length - 1].messages.push(msg);
    }

    return groups;
  }, [conversation]);

  if (!conversation) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col h-full"
    >
      {/* Header */}
      <div
        className={cn(
          "flex items-center gap-3 px-3 py-3 shrink-0",
          "border-b border-white/[0.06] dark:border-white/[0.06] border-black/[0.06]",
        )}
      >
        {/* Back button */}
        <button
          onClick={onBack}
          aria-label="Kembali"
          className={cn(
            "flex items-center justify-center w-8 h-8 rounded-full shrink-0",
            "text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary",
            "hover:bg-white/[0.06] dark:hover:bg-white/[0.06] hover:bg-black/[0.04]",
            "transition-colors duration-150",
          )}
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.8} />
        </button>

        {/* Avatar */}
        <div className="relative shrink-0">
          <div
            className={cn(
              "flex items-center justify-center w-9 h-9 rounded-full",
              "bg-gold/20 text-gold font-semibold text-xs",
              "border border-gold/30",
            )}
          >
            {getInitials(conversation.customerName)}
          </div>
          {conversation.isOnline && (
            <span
              className={cn(
                "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full",
                "bg-green-500 border-[2px] border-dark-bg dark:border-dark-bg border-light-bg",
              )}
            />
          )}
        </div>

        {/* Name & status */}
        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              "text-sm font-semibold truncate",
              "text-dark-text-primary dark:text-dark-text-primary text-light-text-primary",
            )}
          >
            {conversation.customerName}
          </h3>
          <p className="text-[11px] text-dark-text-secondary/70 dark:text-dark-text-secondary/70 text-light-text-secondary/70">
            {conversation.isTyping
              ? "Mengetik..."
              : conversation.isOnline
                ? "Online"
                : "Offline"}
          </p>
        </div>

        {/* Call button */}
        <button
          aria-label="Panggil"
          className={cn(
            "flex items-center justify-center w-9 h-9 rounded-full shrink-0",
            "text-gold",
            "hover:bg-gold/10",
            "transition-colors duration-150",
          )}
        >
          <Phone className="w-4 h-4" strokeWidth={1.8} />
        </button>

        <div className="flex items-center gap-2 ml-auto">
          {isAdmin && (
            <button
              onClick={() => toggleAI(conversation.id)}
              className={cn(
                "px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all",
                conversation.aiEnabled
                  ? "bg-gold/15 text-gold border border-gold/20"
                  : "bg-dark-text-secondary/10 text-dark-text-secondary/50 border border-transparent"
              )}
              title={conversation.aiEnabled ? "AI aktif — klik untuk nonaktifkan auto-reply" : "AI nonaktif — klik untuk aktifkan auto-reply"}
            >
              🤖 {conversation.aiEnabled ? "AI ON" : "AI OFF"}
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 scrollbar-none"
      >
        <div className="flex flex-col gap-1.5">
          {dateGroups.map((group) => (
            <div key={group.label} className="flex flex-col gap-1.5">
              {/* Date separator */}
              <div className="flex items-center justify-center my-3">
                <span
                  className={cn(
                    "px-3 py-0.5 rounded-full text-[11px] font-medium",
                    "bg-white/[0.04] dark:bg-white/[0.04] bg-black/[0.03]",
                    "text-dark-text-secondary/70 dark:text-dark-text-secondary/70 text-light-text-secondary/70",
                    "border border-white/[0.05] dark:border-white/[0.05] border-black/[0.05]",
                  )}
                >
                  {group.label}
                </span>
              </div>

              {group.messages.map((msg, idx) => (
                <ChatBubble
                  key={msg.id}
                  message={msg}
                  isLast={idx === group.messages.length - 1}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Typing indicator */}
        <AnimatePresence>
          {conversation.isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              className="mt-2 ml-1"
            >
              <TypingIndicator />
            </motion.div>
          )}
        </AnimatePresence>

        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="px-3 pb-3 shrink-0">
        <ChatInput />
      </div>
    </motion.div>
  );
}
